import React, {Component} from "react"
import PropTypes from "prop-types"
import {LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer} from "recharts"
import moment from "moment"

import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {H4, Body, Label} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import theme from "@irati/ui/src/global/theme"
import {UsageReport} from "@irati/types"
import emptyGraph from "@irati/ui/images/empty-graph.svg"
import ButtonToggle from "@irati/ui/src/ButtonToggle"
import Loading from "@irati/ui/src/Loading"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import History from "@irati/utils/history"
import Button from "@irati/ui/src/Button"
import adminTracking, {ViewType, Filter} from "@irati-tracking/admin/admin"

import {ANALYTICS_BASE} from "../../../routes/routeConfig"
import {
  Square,
  Circle,
  Triangle,
  Pentagon,
  Rect,
  ActiveSquare,
  ActiveCircle,
  ActiveTriangle,
  ActivePentagon,
  ActiveRect,
  CoordinateProps} from "./Dots"
import Legends from "./Legends"
import CustomTooltip from "./CustomTooltip"
import {Wrapper, HighlightedCard, HightlightedCardH4} from "./styled"
import {ScatterLineKeys, LAST_SUFFIX} from "./Keys"

const LAST_LOGIN_TYPE = "LAST_LOGIN"
const PERKS_TYPE = "PERKS"
const CELEBRATION_TYPE = "RECOGNITION"
const WELLNESS_TYPE = "WELLNESS"
const CULTURE_HUB_TYPE = "CULTURE"

enum Interval {
  WEEK = "WEEK",
  MONTH = "MONTH"
}

type Closest = {
  key: string,
  value: number,
  valueConvertedToYCoordinate: number,
  date: string
}

const LineProps = {
  strokeWidth: 2,
  type: "monotone"
}

const LastLineProps = {
  ...LineProps,
  strokeDasharray: "3 3"
}

const ANIMATION_DURATION = 1500

interface Props extends InjectIntl, WithAuth {}

interface State {
  closest: Closest|null,
  interval: Interval,
  load: Promise<void> | null,
  reports?: UsageReport[],
  isAnimationTransitioning: boolean,
  animationBegin: number,
  maxYValue: number
}

class DashboardGraph extends Component<Props> {
  static contextTypes = {
    apps: PropTypes.object,
    appPath: PropTypes.string.isRequired
  }

  state: State = {
    closest: null,
    interval: Interval.WEEK,
    load: null,
    reports: undefined,
    isAnimationTransitioning: true,
    animationBegin: ANIMATION_DURATION,
    maxYValue: 0
  }

  componentDidMount() {
    const load = this.getUsageReports()
    this.setState({load})
  }

  getUsageReports = () => {
    const {interval} = this.state
    const uuid = authentication.business().uuid
    this.setState({isAnimationTransitioning: true})
    return adminReportingService.getUsageReports(uuid, undefined, interval).then(({reports}) => {
      const allYValues = [].concat(...reports.map(({points}) => {
        return points.map(({value}) => value)
      }))
      const maxYValue = allYValues.reduce((acc, curr) =>curr > acc ? curr : acc)
      this.setState({reports, maxYValue})
    })
  }

  buildReport = (index: number) => {
    const {reports, interval} = this.state
    const overall = reports.find((report) => report.topic === LAST_LOGIN_TYPE)
    const perks = reports.find((report) => report.topic === PERKS_TYPE)
    const celebration = reports.find((report) => report.topic === CELEBRATION_TYPE)
    const wellness = reports.find((report) => report.topic === WELLNESS_TYPE)
    const cultureHub = reports.find((report) => report.topic === CULTURE_HUB_TYPE)
    const originalDate = reports[0].points[index].date

    const date = interval === Interval.WEEK
      ? moment(reports[0].points[index].date).format("MMM Do")
      : moment(reports[0].points[index].date).subtract(1, "months").format("MMM")

    let data = {}

    if (index !== 5) {
      data = {
        overall: Math.round(overall.points[index].value),
        perks: Math.round(perks.points[index].value),
        celebration: Math.round(celebration.points[index].value),
        wellness: Math.round(wellness.points[index].value),
        cultureHub:  Math.round(cultureHub.points[index].value)
      }
    }
    if (index === 4 || index === 5 ) {
      data = {
        ...data,
        overallLast: Math.round(overall.points[index].value),
        perksLast: Math.round(perks.points[index].value),
        celebrationLast: Math.round(celebration.points[index].value),
        wellnessLast: Math.round(wellness.points[index].value),
        cultureHubLast: Math.round(cultureHub.points[index].value)
      }
    }

    return {
      date,
      originalDate,
      ...data
    }
  }

  buildReports = () => {
    return [
      this.buildReport(0),
      this.buildReport(1),
      this.buildReport(2),
      this.buildReport(3),
      this.buildReport(4),
      this.buildReport(5)
    ]
  }

  getOverallUsage = () => {
    const {reports} = this.state
    if (!reports) {
      return 0
    }
    const overall = reports.find((report) => report.topic === LAST_LOGIN_TYPE)
    return Math.round(overall.points[overall.points.length - 2].value || 0)
  }

  setClosestDot = ({activePayload, chartY}) => {
    if (activePayload) {
      const [date, originalDate, ...payload] = Object.entries(activePayload[0].payload).map(([key, value]) => {
        return {key, value}
      })

      const MAX_Y_VALUE_LENGTH = 450
      const RECHARTS_SCALED_TO_VALUE = 1.1
      const PERCENTAGE_TO_Y_VALUE = MAX_Y_VALUE_LENGTH / (this.state.maxYValue * RECHARTS_SCALED_TO_VALUE)
      const yAxisCoordinatedArray = payload.map(({key, value}) => {
        return {
          key,
          value,
          date: date.value,
          originalDate: originalDate.value,
          valueConvertedToYCoordinate: MAX_Y_VALUE_LENGTH - (value * PERCENTAGE_TO_Y_VALUE)
        }
      })
      const closest = yAxisCoordinatedArray.reduce((acc, curr) =>
        Math.abs(curr.valueConvertedToYCoordinate - chartY) < Math.abs(acc.valueConvertedToYCoordinate - chartY) ? curr : acc
      )
      this.setState({closest})
    }
  }

  setOpacity = (line) => {
    const {closest} = this.state
    if (!closest || closest.key === line || closest.key === `${line}${LAST_SUFFIX}`) return 1
    return 0.5
  }

  handleWeekClick = () => {
    adminTracking.reporting({type: "home_Graph", viewType: ViewType.filtered, filter: Filter.date})
    this.setState({interval: Interval.WEEK, animationBegin: 0}, this.getUsageReports)
  }

  handleMonthClick = () => {
    adminTracking.reporting({type: "home_Graph", viewType: ViewType.filtered, filter: Filter.date})
    this.setState({interval: Interval.MONTH, animationBegin: 0}, this.getUsageReports)
  }

  buildLineProps = (
    key: string,
    stroke: string,
    Dot: (props: CoordinateProps) => JSX.Element,
    ActiveDot: (props: CoordinateProps) => JSX.Element) => {
    const {closest} = this.state
    return {
      ...LineProps,
      strokeOpacity: this.setOpacity(key),
      dataKey: key,
      stroke,
      dot: (props) => props.index !== 5 && <Dot {...props} opacity={this.setOpacity(key)} />,
      activeDot: closest?.key === key ? <ActiveDot /> : false
    }
  }

  buildLastLineProps = (
    key: string,
    lastKey: string,
    stroke: string,
    Dot: (props: CoordinateProps) => JSX.Element,
    ActiveDot: (props: CoordinateProps) => JSX.Element) => {
    const {closest, animationBegin} = this.state

    return {
      ...LastLineProps,
      strokeOpacity: this.setOpacity(key),
      animationBegin,
      dataKey: lastKey,
      stroke,
      dot: (props) => props.index === 5 && <Dot {...props} opacity={this.setOpacity(key)} />,
      activeDot: closest?.key === lastKey ? <ActiveDot /> : false
    }
  }

  onRedirect = () => {
    History.push(
      {
        pathname: ANALYTICS_BASE,
        state: {source: "Admin_Home_Graph"}
      }, this.context
    )
  }

  render(){
    const {formatMessage} = this.props
    const {closest, load, reports, interval, isAnimationTransitioning} = this.state
    const data = reports && this.buildReports()
    const xAxisText = interval === Interval.WEEK
      ? "admin.components.dashboardGraph.week.xaxis"
      : "admin.components.dashboardGraph.month.xaxis"
    const overallUsageCardText = interval === Interval.WEEK
      ? "admin.components.dashboardGraph.overallUsageCard.week.body1"
      : "admin.components.dashboardGraph.overallUsageCard.month.body1"

    return <Loading load={load}>
      <Wrapper id="adminSection_dashboardGraph">
        <Flex justifyContent="flex-end">
          <Button cta id="adminSection_viewDetailedReportLink" onClick={this.onRedirect}>
            <FormattedMessage id="admin.components.dashboardGraph.reports" />
          </Button>
          <Icon icon={IconTypes.arrowRight} marginLeft color={theme.colors.primary1} />
        </Flex>
        <H4 marginBottom={1}>
          <FormattedMessage id="admin.components.dashboardGraph.title" />
        </H4>
        <Body marginBottom={2}>
          <FormattedMessage id="admin.components.dashboardGraph.subtitle" />
        </Body>
        <Flex marginBottom={4} justifyContent="flex-end">
          <ButtonToggle
            firstButtonId="buttonToggle_firstButton"
            secondButtonId="buttonToggle_secondButton"
            onClickFirstButton={this.handleWeekClick}
            onClickSecondButton={this.handleMonthClick}
            firstButtonText="admin.components.dashboardGraph.buttonToggle.week"
            secondButtonText="admin.components.dashboardGraph.buttonToggle.month" />
        </Flex>
        <HighlightedCard marginBottom={4} padding={[2, 4]}>
          <Label large>
            <FormattedMessage id="admin.components.dashboardGraph.overallUsageCard.title" />
          </Label>
          <div>
            <Body display="inline-block" marginTop={1}>
              <FormattedMessage id={overallUsageCardText} />
              <HightlightedCardH4 display="inline-block" margin={[0, 1]}>{this.getOverallUsage()}%</HightlightedCardH4>
              <FormattedMessage id="admin.components.dashboardGraph.overallUsageCard.body2" />
            </Body>
          </div>
        </HighlightedCard>
        <ResponsiveContainer width="100%" height={500}>
          <LineChart
            width={500}
            height={300}
            data={data}
            onMouseMove={!isAnimationTransitioning && this.setClosestDot}
            onMouseLeave={() => this.setState({closest: null})}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 20
            }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              padding={{left: 10}}
              axisLine={false}
              tickLine={false}
              label={{value: formatMessage({id: xAxisText}), position: "bottom"}} />
            <YAxis
              tickCount={6}
              tickFormatter={(item) => `${item}%`}
              padding={{bottom: 10}}
              axisLine={false}
              tickLine={false}
              label={{value: formatMessage({id: "admin.components.dashboardGraph.yaxis"}), angle: -90, position: "insideLeft"}} />
            <Tooltip cursor={false} content={<CustomTooltip closest={closest} isMonthly={interval === Interval.MONTH} />} />
            <Line {...this.buildLineProps(ScatterLineKeys.OVERALL, theme.colors.primary2, Square, ActiveSquare)} />
            <Line {...this.buildLastLineProps(
              ScatterLineKeys.OVERALL,
              ScatterLineKeys.OVERALL_LAST,
              theme.colors.primary2,
              Square,
              ActiveSquare)} />
            <Line {...this.buildLineProps(ScatterLineKeys.PERKS, theme.colors.primary1, Circle, ActiveCircle)} />
            <Line {...this.buildLastLineProps(
              ScatterLineKeys.PERKS,
              ScatterLineKeys.PERKS_LAST,
              theme.colors.primary1,
              Circle,
              ActiveCircle)} />
            <Line {...this.buildLineProps(ScatterLineKeys.CELEBRATION, theme.colors.tertiary.p_400, Triangle, ActiveTriangle)} />
            <Line {...this.buildLastLineProps(
              ScatterLineKeys.CELEBRATION,
              ScatterLineKeys.CELEBRATION_LAST,
              theme.colors.tertiary.p_400,
              Triangle,
              ActiveTriangle)} />
            <Line {...this.buildLineProps(ScatterLineKeys.WELLNESS, theme.colors.tertiary.i_400, Pentagon, ActivePentagon)} />
            <Line {...this.buildLastLineProps(
              ScatterLineKeys.WELLNESS,
              ScatterLineKeys.WELLNESS_LAST,
              theme.colors.tertiary.i_400,
              Pentagon,
              ActivePentagon)} />
            <Line {...this.buildLineProps(
              ScatterLineKeys.CULTURE_HUB,
              theme.colors.secondary1,
              Rect,
              ActiveRect)} />
            <Line
              onAnimationEnd={() => this.setState({isAnimationTransitioning: false})}
              {...this.buildLastLineProps(
                ScatterLineKeys.CULTURE_HUB,
                ScatterLineKeys.CULTURE_HUB_LAST,
                theme.colors.secondary1,
                Rect,
                ActiveRect)} />
          </LineChart>
        </ResponsiveContainer>
        {!data &&
        <Flex direction="column" alignItems="center" padding={[15, 0]}>
          <img src={emptyGraph} alt="emptyGraph" />
          <Body marginTop={2} color={theme.colors.utility.subdued}>
            <FormattedMessage id="admin.components.dashboardGraph.empty.body1" />
          </Body>
          <Body marginBottom={2} color={theme.colors.utility.subdued}>
            <FormattedMessage id="admin.components.dashboardGraph.empty.body2" />
          </Body>
          <ButtonRouterLink primary to={ANALYTICS_BASE}>
            <FormattedMessage id="admin.components.dashboardGraph.empty.button" />
          </ButtonRouterLink>
        </Flex>
        }
        {data && <Legends />}
      </Wrapper>
    </Loading>
  }
}

export default injectIntl(DashboardGraph)
